import React, { useState } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { useUser } from '../context/UserContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const QuickContactDialog = ({ open, onOpenChange, companyId, companyName, onSuccess }) => {
  const { currentUser } = useUser();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    position: '',
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.first_name.trim()) {
      toast.error('El nombre del contacto es obligatorio');
      return;
    }

    setLoading(true);
    try {
      const payload = {
        first_name: formData.first_name.trim(),
        last_name: formData.last_name.trim() || null,
        email: formData.email || null,
        phone: formData.phone || null,
        position: formData.position || null,
        company_id: companyId,
      };

      const response = await axios.post(`${API}/contacts`, payload, {
        headers: { 'X-User-Email': currentUser?.email }
      });
      
      toast.success('Contacto creado');
      onSuccess(response.data);
      onOpenChange(false);
      setFormData({ first_name: '', last_name: '', email: '', phone: '', position: '' });
    } catch (error) {
      console.error('Error creating contact:', error);
      toast.error(error.response?.data?.detail || 'Error al crear el contacto');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md max-h-[90vh] overflow-y-auto" data-testid="quick-contact-dialog">
        <DialogHeader>
          <DialogTitle>Nuevo Contacto</DialogTitle>
          {companyName && (
            <p className="text-sm text-muted-foreground">Para: {companyName}</p>
          )}
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="contact-first-name">Nombre *</Label>
              <Input
                id="contact-first-name"
                value={formData.first_name}
                onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                placeholder="Nombre"
                data-testid="quick-contact-first-name"
                autoFocus
              />
            </div>
            
            <div>
              <Label htmlFor="contact-last-name">Apellidos</Label>
              <Input
                id="contact-last-name"
                value={formData.last_name}
                onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                placeholder="Apellidos"
                data-testid="quick-contact-last-name"
              />
            </div>
          </div>
          
          <div>
            <Label htmlFor="contact-email">Email</Label>
            <Input
              id="contact-email"
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              placeholder="email@ejemplo.com"
              data-testid="quick-contact-email"
            />
          </div>
          
          <div>
            <Label htmlFor="contact-phone">Teléfono</Label>
            <Input
              id="contact-phone"
              type="tel"
              value={formData.phone}
              onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              placeholder="+34 600 000 000"
              data-testid="quick-contact-phone"
            />
          </div>
          
          <div>
            <Label htmlFor="contact-position">Cargo</Label>
            <Input
              id="contact-position"
              value={formData.position}
              onChange={(e) => setFormData({ ...formData, position: e.target.value })}
              placeholder="Director, Gerente, etc."
              data-testid="quick-contact-position"
            />
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancelar
            </Button>
            <Button type="submit" disabled={loading} data-testid="quick-contact-save">
              {loading ? 'Creando...' : 'Crear Contacto'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default QuickContactDialog;
